// frontend/src/components/upsell/upsellStyles.js

import React from "react";
import { Box, Typography } from "@mui/material";

import {
  TONE_ACCENT,
  TONE_CTA_INK,
  TAP_TARGET_MIN,
  VARIANT_STYLES,
} from "../leads/leadsStateStyles";
import { colors, typography } from "../../theme/tokens";

/**
 * The upsell family - one vocabulary for the four selling surfaces (navbar
 * chip, dashboard offer banner, last-days banner, checkout offer line).
 *
 * WHY THIS FILE EXISTS. The four surfaces were built before the Ledger state
 * card and the matte welcome dialog settled this product's design language, and
 * every one of them re-derived its own chrome: a turquoise gradient button
 * (white-on-gradient measured 1.64:1 and 2.98:1 - failing AA outright, the
 * exact pair the dialog round rejected), a four-point sparkle the homepage does
 * not use anywhere, a saturated 3px side bar that reads "alert strip", and the
 * offer's own numbers set at the same weight as the words around them. None of
 * them imported a single token from the picked vocabulary. That, more than any
 * one colour, is why they looked like a different product.
 *
 * THE RULE HERE IS: IMPORT, NEVER RE-DECLARE. Every ink, every tap floor and
 * the ceramic shell come from `leadsStateStyles`; every hue and face comes from
 * `theme/tokens`. Nothing in this file types a hex that is not a composition of
 * those, and no call site (Navbar included) owns a colour - they ask for a
 * recipe by variant and tone.
 *
 * THE INVARIANTS, shared by both treatments - only the SHELL differs:
 *   - ink: primary CTA is `solidCtaSx(TONE_CTA_INK[tone])`, a flat AA-safe
 *     fill, never a gradient;
 *   - the figure: the offer's number is a tabular hero numeral with a caption,
 *     the Ledger's identity, because the percent and the saving are the whole
 *     reason these surfaces exist. No count-up - a discount that ticks upward
 *     is a slot machine, and this number is a fact;
 *   - no glyphs: the figure cell takes the badge's slot, and tone is carried by
 *     the accent hairline and the CTA ink (the `TONE_ACCENT`/`TONE_CTA_INK`
 *     split: chrome hue vs button ink);
 *   - 20px radius, 1px hairline, neutral two-layer shadow, 44/46px targets;
 *   - logical properties only, so RTL needs no mirrored copy of anything;
 *   - motion is `.lfs-root .lfs-rise` and nothing else, so the accessibility
 *     widget's "stop animations" already governs these surfaces.
 */

/** The two-layer neutral shadow shared by the dialog and the state card. */
const FAMILY_SHADOW = `
  0 8px 32px rgba(20, 30, 45, 0.06),
  0 2px 6px rgba(20, 30, 45, 0.04)
`;

/** Between the dialog's 20 and the state card's 24 - the family's own value. */
const FAMILY_RADIUS = "20px";

/** The receipt hairline, the one rule used for every internal division. */
export const HAIRLINE = `1px solid ${colors.primary.alpha18}`;

/**
 * Title / body / dismiss faces, shared by every treatment.
 *
 * The body is `colors.text.label` (#666), NEVER `colors.text.secondary`
 * (#757575): the dialog round measured the latter at 4.29:1 on this product's
 * tinted grounds, below the AA floor, while #666 clears 5:1 on every ground
 * used here. Same reasoning, same value, so the two surfaces cannot drift.
 */
export const UPSELL_TITLE_SX = {
  fontFamily: typography.fontFamily.heading,
  fontSize: { xs: "15px", sm: "16px" },
  fontWeight: 700,
  letterSpacing: "-0.005em",
  lineHeight: 1.35,
  color: colors.text.heading,
  m: 0,
};

export const UPSELL_BODY_SX = {
  fontFamily: typography.fontFamily.body,
  fontSize: { xs: "13.5px", sm: "14.5px" },
  lineHeight: 1.65,
  color: colors.text.label,
  // The body WRAPS at every width. The old surface clamped it to one nowrap
  // line at xs, which is why a three-line clamp had to be smuggled in as a
  // node; a sentence that states a deadline must not be truncated on the device
  // most people read it on.
  m: 0,
};

/**
 * The dismiss control: a quiet TEXT control on the action row, inside the card.
 *
 * It used to hang 4px under the card as a centred orphan label, and only
 * because the old shell made the entire card a single `<button>` - nothing
 * interactive could live inside it. That was a structural constraint, not a
 * style, and it disappears with the shell. No icon: the X was the last glyph
 * left on these surfaces and the family has none.
 */
export const UPSELL_DISMISS_SX = {
  minHeight: { xs: TAP_TARGET_MIN, sm: 38 },
  paddingInline: 1.5,
  borderRadius: "10px",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  font: "inherit",
  fontFamily: typography.fontFamily.body,
  fontSize: { xs: "13.5px", sm: "13px" },
  fontWeight: 600,
  color: colors.text.label,
  transition: "background-color 0.2s ease, color 0.2s ease",
  "@media (hover: hover)": {
    "&:hover": {
      backgroundColor: "rgba(20, 30, 45, 0.05)",
      color: colors.text.heading,
    },
  },
  "&:focus-visible": {
    outline: `2px solid ${TONE_CTA_INK.success}`,
    outlineOffset: 2,
  },
};

// ---------------------------------------------------------------- the figure

/**
 * The hero numeral + caption.
 *
 * A DELIBERATE DUPLICATE of the private `Figure` in `LeadsStateCard.js`, and
 * the end state is still one exported component used by both. The note this
 * replaces said "that file is mid-task in another workstream, consolidate when
 * it lands" - it has landed, so the reason is now the narrower one: the merge
 * moves a component OUT of `LeadsStateCard.js`, which is outside this family's
 * folder, and it is a follow-up rather than part of the upsell cleanup.
 *
 * The two are deliberately identical in face so that merge stays a delete
 * rather than a reconciliation - the only addition here is `letterSpacing`,
 * which the family's larger sizes need and which is harmless at the card's
 * smaller ones. Keep them identical, or the follow-up stops being free.
 *
 * @param {object} p
 * @param {string|number} p.value the number itself, pre-formatted
 * @param {string} p.caption      one or two words under it
 * @param {string} p.ink          numeral colour - heading ink or the tone's CTA ink
 * @param {object} p.scale        `{ xs, sm }` font sizes from the variant.
 *   The xs value is ~20% below the sm one ON PURPOSE: at phone width the
 *   numeral shares the headline's row, and a hero does not have to be its
 *   desktop size to out-rank a 15px title - 1.5rem against 15px is still a
 *   1.6x register change, and the extra 8px it gives back is what keeps the
 *   title column above 200px.
 */
export const UpsellFigure = React.memo(function UpsellFigure({
  value,
  caption,
  ink,
  scale,
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        component="div"
        data-fig="value"
        sx={{
          fontFamily: typography.fontFamily.heading,
          fontWeight: 700,
          fontSize: scale,
          lineHeight: 1.05,
          color: ink,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.02em",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
      {caption && (
        <Typography
          component="div"
          data-fig="caption"
          sx={{
            fontFamily: typography.fontFamily.body,
            fontSize: "11.5px",
            color: colors.text.label,
            letterSpacing: "0.015em",
            whiteSpace: "nowrap",
            mt: "2px",
          }}
        >
          {caption}
        </Typography>
      )}
    </Box>
  );
});

// ---------------------------------------------------------------- treatments

/**
 * THE THIRD TREATMENT IS GONE. "Note" - the dialog's matte tint plus a brand
 * hairline - was the warmest of the three the owner compared on 2026-09-20. He
 * picked Receipt for the chip and Ceramic at 50% for every other surface, so
 * Note had no production surface left: it survived only because the comparison
 * gallery was still mounted, exactly like the two deleted ceramic strengths,
 * the two deleted sweep strengths and the deleted `matte` dialog variant. It
 * was deleted with them (git history has it if the comparison is ever re-run).
 *
 * Two things went with it, because it was their only source:
 *   - the TREATMENT'S `edgeFor` hook and the surfaces' top-edge elements. Note
 *     was the only treatment that ever answered anything but "none", so both
 *     render branches were unreachable and the banner's `TopEdge` painted
 *     nothing on every state this product ships;
 *   - nothing else: the tinted CHIP skin Note used to name is still live and
 *     still shipped - see `chipSkin`, where it is now named for the treatment
 *     that actually wears it.
 */

/**
 * T1 - "Receipt". White ground and the figure cell drawn as a receipt line -
 * the Ledger's own hairline row, scoped to the cell.
 *
 * The numeral takes the tone's CTA ink instead of heading ink, making it the
 * only colour on the surface besides the button. The driest and most mature of
 * the treatments, which is the adjective the owner used.
 *
 * SACRIFICE: least warmth. The offer can be missed on a busy dashboard, and it
 * does not carry the mint-card decision from the email round.
 */
const RECEIPT = {
  id: "receipt",
  name: "Receipt",
  bodyInk: colors.text.label,
  figureScale: { xs: "1.4rem", sm: "2rem" },
  figureInk: (tone) => TONE_CTA_INK[tone] ?? TONE_CTA_INK.info,
  figureCell: () => ({
    borderTop: HAIRLINE,
    borderBottom: HAIRLINE,
    py: { xs: 0.75, sm: 1 },
  }),
  shell: () => ({
    position: "relative",
    overflow: "hidden",
    background: colors.background.default,
    border: `1px solid ${colors.primary.alpha20}`,
    borderRadius: FAMILY_RADIUS,
    boxShadow: FAMILY_SHADOW,
  }),
};

/**
 * T2 - "Ceramic". The Ledger shell in miniature.
 *
 * `VARIANT_STYLES.ledger.shell(accent)` imported and overridden for a banner's
 * geometry - the wash, the hairline and the inset highlight are the state
 * card's, not a second attempt at them.
 *
 * SACRIFICE: on an empty dashboard this banner sits directly above a Ledger
 * state card - two ceramic objects, one wash each, and the banner steals the
 * card's identity. Heaviest paint of the treatments.
 */
const CERAMIC = {
  id: "ceramic",
  name: "Ceramic",
  /**
   * THE WASH TAKES HEADING INK FOR BODY TEXT, not `colors.text.label`.
   *
   * MEASURED, and it is why this is not an assumption: on the ceramic ground
   * #666 sits at the family's floor, and weakening the wash did NOT improve it
   * - at 75% strength on the `error` tone it measured 4.33:1 in English at
   * 1100 and 4.48:1 in Hebrew at 360, both UNDER the AA floor. (Weaker alpha
   * lightens most of the gradient but moves the band the text sits over, so
   * "fainter" and "safer" are not the same thing.)
   *
   * This is the welcome dialog's own precedent, at almost the same number: its
   * done-step paragraphs switched to `colors.text.heading` when #666 measured
   * 4.30:1 on that gradient's strongest band, while #333 cleared 8:1 on the
   * same pixels. Applied to the WHOLE ceramic family, not just the failing
   * strength, so the three remain comparable on everything except how loud the
   * ground is - which is the only question the owner is being asked.
   *
   * The title/body hierarchy survives: 700 against 400 at a 1px size step.
   */
  bodyInk: colors.text.heading,
  figureScale: { xs: "1.5rem", sm: "2.25rem" },
  figureInk: () => colors.text.heading,
  figureCell: () => ({ borderInlineEnd: HAIRLINE }),
  atmosphere: true,
  shell: (tone) => ceramicShell(tone, 1),
};

/**
 * Scale every ALPHA in a CSS value, leaving hue and geometry untouched.
 *
 * This is what "the same colour, weaker" has to mean. The alternatives were
 * considered and rejected: moving the gradient's STOP POSITIONS would slide the
 * wash across the card, which is a different composition rather than a fainter
 * one; and lightening the hues toward white would drift them - the accents are
 * already pastel, and a pastel lightened is a different colour, not the same
 * colour turned down. Alpha is the only channel that means "less of exactly
 * this", and because the ground underneath is white, less alpha reads as a
 * paler version of the identical tint.
 *
 * It transforms the LEDGER SHELL'S OWN STRING rather than restating it, so the
 * weak variants track the picked shell if it is ever re-tuned, and the family
 * still has one source for the ceramic wash.
 *
 * Handles both notations the shell uses: 8-digit hex (`#RRGGBBAA`, the accent
 * stops) and `rgba()` (the brand alpha tokens). 6-digit hex has no alpha to
 * scale and is left alone - that is the white midpoint, which must stay white.
 */
const scaleAlpha = (value, factor) => {
  if (typeof value !== "string") return value;
  return value
    .replace(/#([0-9a-f]{6})([0-9a-f]{2})(?![0-9a-f])/gi, (match, rgb, alpha) => {
      const scaled = Math.round(parseInt(alpha, 16) * factor);
      return `#${rgb}${Math.max(0, Math.min(255, scaled))
        .toString(16)
        .padStart(2, "0")}`;
    })
    .replace(/rgba\(([^)]+)\)/gi, (match, inner) => {
      const parts = inner.split(",").map((part) => part.trim());
      if (parts.length < 4) return match;
      const alpha = Number(parts[3]) * factor;
      return `rgba(${parts[0]}, ${parts[1]}, ${parts[2]}, ${alpha.toFixed(4)})`;
    });
};

/**
 * Weaken a `background` whatever SHAPE the borrowed shell states it in.
 *
 * The ground comes from another module (`VARIANT_STYLES.ledger.shell`), and
 * this used to read `background.xs` / `background.sm` by name. That is a silent
 * failure waiting to happen: a shell that ever states one flat string instead
 * of a responsive object would make both keys `undefined`, and `scaleAlpha`
 * returns a non-string unchanged - so every ceramic surface in the family, both
 * banners AND the checkout line, would render with NO GROUND AT ALL, border and
 * shadow only, with nothing thrown and nothing logged. A missing wash is the
 * hardest kind of bug to notice, because the surface still looks deliberate.
 *
 * So: a plain string is scaled as a string, an object has EVERY breakpoint key
 * scaled (a new `md` stop travels for free), and anything else keeps the
 * borrowed ground at full strength - too loud is a visible, reportable bug,
 * absent is not - while saying so out loud in development.
 */
const scaleBackground = (background, strength) => {
  if (typeof background === "string") return scaleAlpha(background, strength);
  if (background && typeof background === "object") {
    return Object.fromEntries(
      Object.entries(background).map(([key, value]) => [
        key,
        scaleAlpha(value, strength),
      ])
    );
  }
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.error(
      "[upsell] ceramicShell: the Ledger shell's `background` is neither a " +
        "string nor an object, so the wash cannot be weakened. Rendering it at " +
        "full strength. Fix `VARIANT_STYLES.ledger.shell` or this scaler.",
      background
    );
  }
  return background;
};

/**
 * The ceramic shell at a given wash STRENGTH (1 = the Ledger's own).
 *
 * The border weakens with the wash - a hairline at full strength around a
 * 50% ground would be the loudest thing left on the card. The shadow does not:
 * it is neutral grey and carries the card's elevation, which is not what
 * "weaker wash" is about.
 */
const ceramicShell = (tone, strength) => {
  const accent = TONE_ACCENT[tone] ?? TONE_ACCENT.info;
  const base = VARIANT_STYLES.ledger.shell(accent);
  return {
    ...base,
    background:
      strength === 1
        ? base.background
        : scaleBackground(base.background, strength),
    border: strength === 1 ? base.border : scaleAlpha(base.border, strength),
    borderRadius: FAMILY_RADIUS,
    textAlign: "start",
    paddingInline: 0,
    py: 0,
  };
};

/**
 * THE CERAMIC CORNER GLOW'S FILL, at a wash strength. One definition, because
 * two surfaces paint this layer: the dashboard banner (`CeramicAtmosphere`) and
 * the checkout offer line.
 *
 * It exists because those two drifted. The checkout line wrote `${accent}33`
 * straight into its gradient and so painted the glow at FULL strength while
 * wearing `ceramic50` - twice the banner's glow under the same variant id, and
 * exactly what the note on `ceramicAtStrength` says must not happen: a
 * full-strength corner glow over a half-strength wash puts the colour straight
 * back. The geometry stays per-surface (same corner, but a 220px circle on a
 * ~94px banner and a 200px one on a ~63px line sit at different offsets); only
 * the colour is shared, because the colour is all the strength governs.
 *
 * @param {string} accent   the tone's accent hex
 * @param {number} strength the treatment's `atmosphereStrength` (1 = full)
 * @returns {string} a `radial-gradient(...)` value
 */
export const ceramicGlowFill = (accent, strength = 1) =>
  `radial-gradient(circle, ${scaleAlpha(`${accent}33`, strength)} 0%, transparent 68%)`;

/**
 * The Ceramic wash, turned down. `ceramic50` is the one the owner picked after
 * comparing 100 / 75 / 50 side by side, and it is what ships on every ceramic
 * surface; the two it beat are deleted. `CERAMIC` above is NOT a variant any
 * more - it stays private as the full-strength BASE this is derived from, so
 * "the same colour, weaker" still has exactly one source.
 *
 * Same treatment in every other respect - figure scale, ink, cell, atmosphere -
 * so the comparison the owner made was only about how loud the ground is.
 * The atmosphere glow scales with it (see `atmosphereStrength`), because a
 * full-strength corner glow over a half-strength wash would put the colour
 * straight back.
 */
const ceramicAtStrength = (id, name, strength) => ({
  ...CERAMIC,
  id,
  name,
  atmosphereStrength: strength,
  shell: (tone) => ceramicShell(tone, strength),
});

const CERAMIC_50 = ceramicAtStrength("ceramic50", "Ceramic 50%", 0.5);

/** The treatments, keyed by the `variant` prop value. */
export const UPSELL_VARIANTS = {
  receipt: RECEIPT,
  ceramic50: CERAMIC_50,
};

/** Every variant id. Both of these ship - see `UPSELL_SURFACE_VARIANT`. */
export const UPSELL_VARIANT_IDS = ["receipt", "ceramic50"];

/**
 * WHICH TREATMENT EACH SURFACE WEARS - the owner's picks of 2026-09-20.
 *
 * He did not choose one treatment for everything; he chose PER SURFACE, and
 * this table is the whole decision. Read it before changing any `variant` prop:
 * a call site that passes its own value is overriding a decision, not making
 * one.
 *
 *   chip          Receipt - the toolbar wants the quietest object there is;
 *                           a white chip with a hairline sits as a peer of the
 *                           plan badge instead of competing with it.
 *   everything else Ceramic AT 50% - the final wash strength, decided after
 *                           comparing 100 / 75 / 50 side by side.
 *
 * SHIPPING STRENGTH IS `ceramic50`, ON EVERY CERAMIC SURFACE. The 100% and 75%
 * washes are deleted - if a new ceramic surface appears it takes this table's
 * value, not a strength of its own.
 */
export const UPSELL_SURFACE_VARIANT = Object.freeze({
  chip: "receipt",
  bannerOffer: "ceramic50",
  bannerState: "ceramic50",
  checkoutLine: "ceramic50",
});

/**
 * @param {keyof UPSELL_SURFACE_VARIANT} surface
 * @returns {string} the variant id for that surface, falling back to the
 *   offer banner's rather than throwing - an unknown surface must still paint.
 */
export const variantForSurface = (surface) =>
  UPSELL_SURFACE_VARIANT[surface] ?? UPSELL_SURFACE_VARIANT.bannerOffer;

/**
 * The banner's own resolver. The two banner states are separate ROWS of the
 * table above, so the owner can give the live offer its own treatment without
 * touching the component - today they both read `ceramic50`, which is why
 * passing the wrong fact here is invisible rather than harmless.
 *
 * `hasOffer` means EXACTLY "this banner is carrying offer numbers". It is NOT
 * "this banner has a figure": the urgent 1-3 day banner is an offer banner and
 * deliberately carries no figure cell (CD-06 already leads with the number), so
 * a caller passing `Boolean(config.figure)` would resolve it as a non-offer
 * state. `SubscriptionBanner` passes `Boolean(config.offerFacts)`.
 *
 * @param {boolean} hasOffer
 * @returns {string} variant id
 */
export const variantForBannerState = (hasOffer) =>
  hasOffer
    ? UPSELL_SURFACE_VARIANT.bannerOffer
    : UPSELL_SURFACE_VARIANT.bannerState;

/**
 * The fallback for `upsellVariant` only. It is NOT "what production renders" -
 * that is the surface table above.
 */
export const UPSELL_DEFAULT_VARIANT = "ceramic50";

/**
 * @param {string} variant
 * @returns {object} the treatment, defaulting to `UPSELL_DEFAULT_VARIANT`
 *   (Ceramic at 50%) - an unknown variant must render a real surface, never
 *   blank the dashboard or the checkout.
 */
export const upsellVariant = (variant) =>
  UPSELL_VARIANTS[variant] ?? UPSELL_VARIANTS[UPSELL_DEFAULT_VARIANT];

// ---------------------------------------------------------------- the chip

/**
 * The navbar upgrade affordance, as a CHIP rather than a button.
 *
 * It was the brightest object in the toolbar and the only gradient in it, which
 * made it read as a promo sticker beside a matte user pill. The box is now the
 * same in every treatment - only the skin differs - and it never lifts on
 * hover, because the user pill beside it does not lift either.
 *
 * @param {string} variant
 * @param {object} p
 * @param {boolean} p.hasOffer  a saving is being advertised
 * @returns {object} sx for the desktop chip
 */
/** The painted chip height, desktop and phone alike. One number, one family. */
export const PILL_PAINTED_HEIGHT = 34;

/**
 * COMPACT is TWO boxes, and that is the whole point.
 *
 * The chip needs a 44px touch target and a painted box smaller than that: a
 * 44px-tall pill beside the hamburger's 40px disc read as a block rather than
 * as its peer. So the INTERACTIVE element is a transparent 44px hit area and
 * the pill inside it is painted at `PILL_PAINTED_HEIGHT` - the same technique
 * the dismiss and close controls in this codebase use, and the reason
 * TAP_TARGET_MIN is documented as "a floor on the BOX, never on the glyph".
 *
 * Growing the padding instead would not work here: the chip has a background,
 * so padding grows the PAINTED box, which is the thing being made smaller.
 */
export const pillHitAreaSx = () => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: TAP_TARGET_MIN,
  paddingInline: 0,
  background: "transparent",
  border: "none",
  cursor: "pointer",
  // The ring hugs the PAINTED pill, not the invisible hit area - an outline
  // round a 44px transparent box would float away from the thing it marks.
  "&:focus-visible": { outline: "none" },
  "&:focus-visible [data-chip-paint]": {
    outline: `2px solid ${TONE_CTA_INK.success}`,
    outlineOffset: 2,
  },
});

export const pillSx = (variant, { hasOffer = false, compact = false } = {}) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.75,
  height: PILL_PAINTED_HEIGHT,
  paddingInline: compact ? 1.25 : 1.5,
  borderRadius: "999px",
  cursor: "pointer",
  // IT MUST BE ABLE TO SHRINK. `maxWidth` was `{ md: 220 }` - md-ONLY - so
  // below that breakpoint the chip had no cap at all, and because its label is
  // `nowrap` the chip's min-content width was its full label. A flex item that
  // cannot shrink does not get clipped, it pushes: measured at 360px, the
  // toolbar's own contents came to 380px in a 326px box and the account pill
  // was shoved clean out of it. `maxWidth: 100%` at every width plus
  // `minWidth: 0` is what makes the
  // chip absorb a squeeze instead of exporting it to its neighbours.
  maxWidth: compact ? "none" : { xs: "100%", md: 220 },
  minWidth: 0,
  flexShrink: compact ? 0 : 1,
  // The hit area owns the focus ring in compact mode (see pillHitAreaSx).
  ...(compact ? { pointerEvents: "none" } : null),
  boxShadow: "none",
  transition: "background-color 0.2s ease, border-color 0.2s ease, filter 0.2s ease",
  ...chipSkin(variant, hasOffer),
  "&:focus-visible": {
    outline: `2px solid ${TONE_CTA_INK.success}`,
    outlineOffset: 2,
  },
});

/**
 * The drawer's row-shaped twin. Same skin, row geometry: it must look like a
 * chip that happens to be a row, not a button bar across the menu.
 */
export const drawerRowSx = (variant, { hasOffer = false } = {}) => ({
  mx: 1,
  my: 0.4,
  // `&.MuiMenuItem-root`, not a bare `minHeight`. MUI's MenuItem sets
  // `min-height: 48px` at xs and `min-height: auto` from sm UP, inside a media
  // query - which outranks a plain declaration here. MEASURED: the row was
  // 33px tall at sm, below the 44px tap floor, and the drawer is shown all the
  // way to `md` (600-899px is a real phone-landscape / small-tablet band), so
  // that was a live target-size failure and not a theoretical one.
  "&.MuiMenuItem-root": { minHeight: `${TAP_TARGET_MIN}px` },
  minHeight: TAP_TARGET_MIN,
  paddingInline: 1.5,
  borderRadius: "12px",
  transition: "background-color 0.2s ease, border-color 0.2s ease, filter 0.2s ease",
  ...chipSkin(variant, hasOffer),
  "&:focus-visible": {
    outline: `2px solid ${TONE_CTA_INK.success}`,
    outlineOffset: -2,
  },
});

/**
 * Skin per treatment. Split out so the chip and the drawer row can never drift.
 *
 * The no-offer chip is the PLAN BADGE's recipe (white ground, hairline, ink) in
 * every treatment except Ceramic's offer state - the toolbar already has
 * exactly one quiet bordered chip next to the logo, and matching it is what
 * makes the upgrade affordance belong there.
 *
 * THE TINTED SKIN IS CERAMIC'S, and saying so is the point. It was written as
 * `case "note": default:` when Note was still a candidate treatment; deleting
 * Note therefore could not be a pure delete, because that branch is live code
 * - it is what a ceramic chip wears, and it is what an unknown variant falls
 * through to (`upsellVariant` already resolves those to `ceramic50`, so the two
 * are the same answer rather than a coincidence). The chip that SHIPS is
 * Receipt; this branch exists for the other ceramic surfaces' vocabulary and
 * for the gallery-less comparison a future round may need.
 */
function chipSkin(variant, hasOffer) {
  const ink = TONE_CTA_INK.success; // #00796B, 5.32:1 on white
  const quietInk = colors.primary.accessible; // #005A52, 8.2:1 on white

  if (!hasOffer) {
    return {
      color: quietInk,
      backgroundColor: colors.background.default,
      border: `1px solid ${colors.primary.alpha30}`,
      "@media (hover: hover)": {
        "&:hover": { backgroundColor: colors.primary.alpha08 },
      },
    };
  }

  switch (upsellVariant(variant).id) {
    case "receipt":
      return {
        color: ink,
        backgroundColor: colors.background.default,
        // The hairline is the only boundary on a white ground, so it carries
        // the 3:1 non-text floor itself: `D9` on #00796B measures 3.77:1, the
        // dialog's own quiet-border alpha.
        border: `1px solid ${ink}D9`,
        "@media (hover: hover)": {
          "&:hover": { backgroundColor: colors.primary.alpha08 },
        },
      };
    case "ceramic50":
    default:
      return {
        color: ink,
        backgroundColor: colors.primary.alpha12,
        border: `1px solid ${colors.primary.alpha30}`,
        "@media (hover: hover)": {
          "&:hover": { backgroundColor: colors.primary.alpha18 },
        },
      };
  }
}

// ---------------------------------------------------------------- the splitter

/**
 * Split an approved label into runs so the NUMBER inside it can be set in
 * tabular 700 while the sentence stays byte-identical.
 *
 * The amount is the reason the chip exists, and inline in a 12.5px/600 label it
 * was indistinguishable from the words around it. This does not rewrite the
 * string: it slices it, so CD-01 (Hebrew or English) is still rendered in full
 * and in order, and a label with no digits comes back as one plain run.
 *
 * A PERCENT SIGN STAYS WITH ITS NUMBER. The friend-code chip reads
 * "20% הנחת חבר": split as "20" + "% הנחת חבר", the "%" became the first
 * character of a separate flex item - its own bidi paragraph, resolved RTL -
 * and was painted on the far side of the number ("%20 הנחת חבר", measured on
 * the desktop navbar). Kept inside the LTR-isolated amount run, "20%" is one
 * unit and renders as written in both directions.
 *
 * @param {string} label
 * @returns {Array<{text: string, isAmount: boolean}>}
 */
export const splitAmount = (label) => {
  if (typeof label !== "string" || !label) return [];
  // A digit run, optionally with a decimal separator inside it - "10.5" is a
  // real percent here, and "1,200" a plausible future saving - and an
  // optional trailing "%" that belongs to the number (see above).
  const parts = label.split(/(\d[\d.,]*\d%?|\d%?)/);
  return parts
    .filter((part) => part !== "")
    .map((part) => ({ text: part, isAmount: /^\d/.test(part) }));
};

/**
 * The LEAD VERB of an approved label - the compact chip's visible text.
 *
 * CD-01 reads "שדרג - חיסכון עד 31 ש\"ח" / "Upgrade - save up to 31 NIS", so
 * this returns "שדרג" / "Upgrade": a SLICE of the approved string taken at its
 * own " - " clause break, never a rewrite and never a shorter sentence
 * invented to fit. The plain no-offer label has no clause break and comes back
 * whole, which is already the same word.
 *
 * It is the visible label ONLY - the control's accessible name stays the full
 * approved sentence. Deriving the visible text FROM that sentence is what
 * makes WCAG 2.5.3 (Label in Name) hold by construction rather than by
 * coincidence: a slice is always a substring.
 *
 * The owner chose the verb over the amount (2026-09-20): a bare number in the
 * app bar reads as a price rather than a saving, and the meaning of the number
 * lives in words the chip has no room for.
 */
export const leadWord = (label) => {
  if (typeof label !== "string" || !label) return "";
  const clause = label.indexOf(" - ");
  if (clause !== -1) return label.slice(0, clause).trim();
  const space = label.indexOf(" ");
  return (space === -1 ? label : label.slice(0, space)).trim();
};

/**
 * The label's OWN reading direction, from its first strong character.
 *
 * WHY IT EXISTS - the bug it closes. `splitAmount` hands the chip three runs,
 * and the chip lays them out as FLEX ITEMS. A flex container orders its items
 * along the main axis by its own `direction`, and bidi reordering does NOT
 * cross a flex item boundary: each item is its own bidi paragraph. The app
 * shell has no `dir` (`<html lang="he">` with no `dir`, no RTL MUI theme), so
 * the container resolved to `ltr` and the three runs were placed LEFT to RIGHT.
 * A Hebrew reader starts at the RIGHT, so the approved
 * `שדרג - חיסכון עד 59 ש"ח` was read back as `ש"ח 59 שדרג - חיסכון עד` -
 * the amount had jumped to the front. MEASURED in a real browser, not reasoned:
 * the prose run sat at x=49 and the currency run at x=159.6.
 *
 * ONE TEXT NODE would have been fine - the UBA resolves the whole sentence
 * correctly even inside an LTR paragraph, which is exactly why the drawer row
 * (a single `ListItemText`) always rendered right. The bug is a property of the
 * SPLIT, so the fix belongs to the split: the element that holds the runs is
 * given the label's own direction, and the runs then flow in reading order.
 *
 * DERIVED FROM THE LABEL, never from the ambient `dir` or an `isHebrew` flag.
 * The chip is a presentational component handed a finished string; deriving the
 * direction from that string is what makes it correct in a container with no
 * `dir`, in one that later gains `dir="rtl"`, and in either language - and it
 * cannot drift from the copy the way a second flag passed beside it could.
 *
 * The rule is the Unicode "first strong character" paragraph rule (UAX #9 P2/P3)
 * restricted to the two scripts this product ships. A string with no strong
 * character at all ("59") has no reading direction of its own, and `ltr` is the
 * CSS initial value - the same answer the browser would give.
 *
 * @param {string} text
 * @returns {"rtl"|"ltr"}
 */
const FIRST_STRONG = /[֐-׿؀-޿יִ-﷿ﹰ-﻿]|[A-Za-zÀ-ɏ]/;
const RTL_STRONG = /[֐-׿؀-޿יִ-﷿ﹰ-﻿]/;

export const textDirection = (text) => {
  if (typeof text !== "string" || !text) return "ltr";
  const match = text.match(FIRST_STRONG);
  if (!match) return "ltr";
  return RTL_STRONG.test(match[0]) ? "rtl" : "ltr";
};

/**
 * The bolded-amount run's face, shared by the chip and the drawer row.
 *
 * ISOLATED. The digits are a foreign-direction island inside a Hebrew sentence,
 * so they are declared as one: `isolate` makes the run an atomic neutral object
 * to whatever surrounds it instead of letting its characters take part in the
 * neighbours' bidi resolution. Today's amounts are bare digits, which resolve
 * correctly either way; a grouped amount ("1,200" - already a `splitAmount`
 * case) or a future suffix is where an un-isolated run starts fragmenting. The
 * run also carries `dir="ltr"` at the call site, because a CSS `unicode-bidi`
 * is ignored on a blockified flex item while the attribute's direction is not.
 */
export const AMOUNT_RUN_SX = {
  fontWeight: 700,
  fontVariantNumeric: "tabular-nums",
  unicodeBidi: "isolate",
};
